import { useEffect, useState } from 'react'

interface Req {
  title: string
  message?: string
  confirmLabel: string
  cancelLabel: string
  danger?: boolean
  resolve: (ok: boolean) => void
}

let current: Req | null = null
const listeners = new Set<(r: Req | null) => void>()
const set = (r: Req | null) => { current = r; listeners.forEach((l) => l(r)) }

export function confirmDialog(opts: Omit<Req, 'resolve'>): Promise<boolean> {
  return new Promise((resolve) => set({ ...opts, resolve }))
}

/** Standard destructive-delete confirm with shared copy. */
export function confirmDelete(t: (k: string) => string): Promise<boolean> {
  return confirmDialog({
    title: t('confirm.deleteTitle'),
    message: t('confirm.deleteBody'),
    confirmLabel: t('common.delete'),
    cancelLabel: t('common.cancel'),
    danger: true,
  })
}

export function ConfirmHost() {
  const [req, setReq] = useState<Req | null>(current)
  useEffect(() => {
    listeners.add(setReq)
    return () => { listeners.delete(setReq) }
  }, [])
  useEffect(() => {
    if (!req) return
    const h = (e: KeyboardEvent) => e.key === 'Escape' && done(false)
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [req])

  if (!req) return null
  const done = (ok: boolean) => { req.resolve(ok); set(null) }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
      <button className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-hidden tabIndex={-1} onClick={() => done(false)} />
      <div className="relative z-10 w-full max-w-sm rounded-3xl bg-brand-50 dark:bg-brand-900 p-6 shadow-lift" role="alertdialog" aria-modal="true" aria-label={req.title}>
        <h2 className="text-lg font-bold">{req.title}</h2>
        {req.message && <p className="mt-2 text-sm text-brand-600 dark:text-brand-300">{req.message}</p>}
        <div className="mt-6 flex gap-3">
          <button onClick={() => done(false)} className="btn-ghost flex-1">{req.cancelLabel}</button>
          <button
            autoFocus
            onClick={() => done(true)}
            className={
              req.danger
                ? 'inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-red-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 active:scale-[0.98]'
                : 'btn-primary flex-1'
            }
          >
            {req.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
